import { Box, Divider, HStack, Text, VStack, useToast } from '@chakra-ui/react';
import { Currency } from '@jurnee/common/src/entities/Currency';
import { PartnerDocumentJSON } from '@jurnee/common/src/entities/PartnerDocument';
import { PartnerPaymentDocumentJSON } from '@jurnee/common/src/entities/PartnerPaymentDocument';
import { RateJSON } from '@jurnee/common/src/entities/Rate';
import { sortByDate } from '@jurnee/common/src/utils/arrays';
import { formatPrice } from '@jurnee/common/src/utils/prices';
import { isEmpty } from '@jurnee/common/src/utils/strings';
import { getErrorToast } from '@jurnee/common/src/utils/toasts';
import { getPaymentDocumentUrl, getWireTransferConfirmationLetterUrl } from 'api/partnerDocuments';
import { PaymentStatus } from 'components/PaymentStatus';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DotMenu } from './DotMenu';
import { PaymentLine } from './PaymentLine';

interface Props {
  partnerDocument: PartnerDocumentJSON;
  rate: RateJSON;
  onDelete(partnerDocument: PartnerDocumentJSON): void;
}

type TimelineItem = {
  id: string;
  type: 'CREDIT_CARD' | 'WIRE_TRANSFER' | 'BANK_CARD';
  date: string;
  amount: number;
  currency: Currency;
  onDownload?: () => void;
};

export function InvoiceCard({ partnerDocument, rate, ...props }: Props) {
  const toast = useToast();
  const { t } = useTranslation('invoice');

  const amountInclTax = partnerDocument.amount + partnerDocument.taxAmount;
  const formattedAmount = formatPrice(amountInclTax, partnerDocument.currency);

  const hasReference = partnerDocument.documentReference && !isEmpty(partnerDocument.documentReference);

  async function onDownload(paymentDocumentId: PartnerPaymentDocumentJSON['id']) {
    try {
      const { url } = await getPaymentDocumentUrl({
        partnerId: partnerDocument.partnerId,
        bookingId: partnerDocument.bookingId,
        partnerDocumentId: partnerDocument.id,
        paymentDocumentId
      });

      window.open(url, '_blank');
    } catch(error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast(getErrorToast(t('paymentDocument.toasts.download.error'), message));
    }
  }

  async function onDownloadWireTransferConfirmation(payoutId: number) {
    try {
      const { url } = await getWireTransferConfirmationLetterUrl({
        partnerId: partnerDocument.partnerId,
        bookingId: partnerDocument.bookingId,
        partnerDocumentId: partnerDocument.id,
        payoutId
      });

      window.open(url, '_blank');
    } catch(error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast(getErrorToast(t('paymentDocument.toasts.download.error'), message));
    }
  }

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const documents: TimelineItem[] = partnerDocument.partnersPaymentDocuments.map(doc => ({
      id: `doc-${doc.id}`,
      type: doc.type,
      date: doc.paidAt,
      amount: doc.amount,
      currency: doc.currency,
      onDownload: () => onDownload(doc.id)
    }));

    const payouts = partnerDocument.partnersPayouts || [];

    const wireTransfers: TimelineItem[] = payouts
      .filter(payout => payout.wireTransfer && payout.wireTransfer.status === 'PAID')
      .map(payout => ({
        id: `wire-${payout.id}`,
        type: 'WIRE_TRANSFER',
        date: payout.wireTransfer!.updatedAt,
        amount: payout.wireTransfer!.receivedAmount ?? payout.wireTransfer!.sentAmount,
        currency: payout.wireTransfer!.receivedCurrency ?? payout.wireTransfer!.sentCurrency,
        onDownload: payout.wireTransfer!.confirmationLetter ? () => onDownloadWireTransferConfirmation(payout.id) : undefined
      }));

    const bankCards: TimelineItem[] = payouts
      .filter(payout => payout.bankCard && payout.bankCard.bankCardTransactions)
      .map(payout => {
        const cardTransactions = payout.bankCard!.bankCardTransactions!;
        if (cardTransactions.length === 0) { return null; }

        const totalAmount = cardTransactions.reduce((total, tx) => total + (tx.fundDirection === 'DEBIT' ? tx.cardAmount : -tx.cardAmount), 0);
        if (totalAmount === 0) { return null; }

        const latestDate = cardTransactions.reduce((max, tx) => new Date(tx.updatedAt) > new Date(max) ? tx.updatedAt : max, cardTransactions[0].updatedAt);

        return {
          id: `card-${payout.id}`,
          type: 'BANK_CARD',
          date: latestDate,
          amount: totalAmount,
          currency: cardTransactions[0].cardCurrency
        };
      })
      .filter(item => item !== null) as TimelineItem[];

    return sortByDate([...documents, ...wireTransfers, ...bankCards], 'date');
  }, [partnerDocument.partnersPaymentDocuments, partnerDocument.partnersPayouts]);

  const hasStartedPayment = timelineItems.length > 0;

  return (
    <VStack w="100%" p={4} bg="white" border="1px solid" borderColor="gray.200" borderRadius={4} justifyContent="space-between">
      <HStack w="100%" justifyContent="space-between">
        <VStack w="100%" spacing={1} alignItems="flex-start">
          <Text fontSize={16} lineHeight="16px" fontWeight={700} noOfLines={1}>{formattedAmount}</Text>
          <Text fontSize={12} lineHeight="12px" color="gray.400" textTransform="uppercase">{ hasReference ? partnerDocument.documentReference : '-' }</Text>
        </VStack>

        <HStack spacing={4}>
          <PaymentStatus partnerDocuments={[partnerDocument]} rate={rate} />

          {
            !hasStartedPayment ?
              <DotMenu onDelete={() => props.onDelete(partnerDocument)} /> :
              <Box minW="32px" />
          }
        </HStack>
      </HStack>

      {
        timelineItems.map(item => (
          <VStack key={item.id} w="100%" spacing={4}>
            <Divider />
            <PaymentLine
              type={item.type}
              amount={item.amount}
              currency={item.currency}
              date={new Date(item.date)}
              onDownload={item.onDownload}
            />
          </VStack>
        ))
      }
    </VStack>
  );
}